import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, Trash2, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { useOfflineStore, REFRESH_INTERVAL_OPTIONS } from '../../../store/useOfflineStore';
import type { BoardOfflineSettings, PrefetchResult } from '../../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  boardId: string;
  boardName: string;
  isSharedBoard: boolean;
}

const DEFAULT_SETTINGS: Omit<BoardOfflineSettings, 'boardId'> = {
  enabled: false,
  mode: 'snapshot',
  refreshIntervalMinutes: 15,
  includeFiles: false,
  lastSnapshotAt: null,
};

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatSnapshotAge(isoDate: string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export const OfflineSettingsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  boardId,
  boardName,
  isSharedBoard,
}) => {
  const { getBoardSettings, saveBoardSettings, snapshotBoard, clearBoardSnapshot, isSnapshotting, snapshotError } =
    useOfflineStore();

  const saved = getBoardSettings(boardId);
  const [settings, setSettings] = useState<BoardOfflineSettings>(
    saved ?? { ...DEFAULT_SETTINGS, boardId },
  );
  const [prefetchMeta, setPrefetchMeta] = useState<Pick<PrefetchResult, 'fileCount' | 'totalFileSize'> | null>(null);
  const [customInterval, setCustomInterval] = useState<string>('');
  const [fileChoiceNeeded, setFileChoiceNeeded] = useState(false);
  const [pendingEnable, setPendingEnable] = useState(false);

  const snapping = isSnapshotting[boardId] ?? false;
  const snapErr = snapshotError[boardId] ?? null;

  useEffect(() => {
    if (!isOpen) return;
    const current = getBoardSettings(boardId);
    setSettings(current ?? { ...DEFAULT_SETTINGS, boardId });
    setPrefetchMeta(null);
    setFileChoiceNeeded(false);
    setPendingEnable(false);
  }, [isOpen, boardId, getBoardSettings]);

  const isCustomInterval = !REFRESH_INTERVAL_OPTIONS.includes(
    settings.refreshIntervalMinutes as (typeof REFRESH_INTERVAL_OPTIONS)[number],
  );

  const handleSave = (overrides?: Partial<BoardOfflineSettings>) => {
    const next = { ...settings, ...overrides };
    saveBoardSettings(next);
    setSettings(next);
  };

  const handleToggleEnable = async () => {
    if (settings.enabled) {
      handleSave({ enabled: false });
      return;
    }
    // About to enable — check file sizes first
    setPendingEnable(true);
    try {
      const res = await fetch(
        `/api/offline/prefetch?boardId=${encodeURIComponent(boardId)}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('kan-do-token')}` } },
      );
      if (!res.ok) throw new Error('Failed to fetch board info');
      const data: PrefetchResult = await res.json();
      setPrefetchMeta({ fileCount: data.fileCount, totalFileSize: data.totalFileSize });
      if (data.fileCount > 0) {
        setFileChoiceNeeded(true);
      } else {
        handleSave({ enabled: true, includeFiles: false });
        void triggerSnapshot({ enabled: true, includeFiles: false });
      }
    } catch {
      handleSave({ enabled: true });
      void triggerSnapshot({ enabled: true });
    } finally {
      setPendingEnable(false);
    }
  };

  const handleFileChoice = (includeFiles: boolean) => {
    setFileChoiceNeeded(false);
    handleSave({ enabled: true, includeFiles });
    void triggerSnapshot({ enabled: true, includeFiles });
  };

  const triggerSnapshot = async (overrides?: Partial<BoardOfflineSettings>) => {
    const merged = { ...settings, ...overrides };
    saveBoardSettings(merged);
    setSettings(merged);
    try {
      await snapshotBoard(boardId);
    } catch {
      // error visible via snapshotError
    }
  };

  const handleClear = async () => {
    await clearBoardSnapshot(boardId);
    handleSave({ enabled: false, lastSnapshotAt: null });
  };

  if (!isOpen) return null;

  const lastSnap = settings.lastSnapshotAt;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2.5">
            <WifiOff className="w-5 h-5 text-indigo-500" />
            <h2 className="font-bold text-slate-800 dark:text-slate-100 text-base">Offline Settings</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Board name */}
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Board: <span className="font-semibold text-slate-700 dark:text-slate-200">{boardName}</span>
          </p>

          {/* Multi-user alert */}
          {isSharedBoard && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>This is a shared board. Other members won't see your offline changes until you sync back online.</span>
            </div>
          )}

          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Offline mode</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Snapshot this board so it works without a connection
              </p>
            </div>
            <button
              type="button"
              onClick={handleToggleEnable}
              disabled={snapping || pendingEnable}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                settings.enabled ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-700'
              } disabled:opacity-50`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                  settings.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* File choice prompt */}
          {fileChoiceNeeded && prefetchMeta && (
            <div className="p-3 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/30 space-y-3">
              <p className="text-xs font-semibold text-indigo-800 dark:text-indigo-300">
                This board has {prefetchMeta.fileCount} file{prefetchMeta.fileCount !== 1 ? 's' : ''} ({formatBytes(prefetchMeta.totalFileSize)})
              </p>
              <p className="text-xs text-indigo-700 dark:text-indigo-400">
                Download files for offline use (larger snapshot), or save metadata only?
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleFileChoice(true)}
                  className="flex-1 py-1.5 rounded-md bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition-colors"
                >
                  Yes, download files
                </button>
                <button
                  type="button"
                  onClick={() => handleFileChoice(false)}
                  className="flex-1 py-1.5 rounded-md bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                >
                  Metadata only
                </button>
              </div>
            </div>
          )}

          {settings.enabled && (
            <>
              {/* Mode selector */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">Snapshot mode</p>
                <div className="grid grid-cols-2 gap-2">
                  {(['snapshot', 'always-ready'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => handleSave({ mode })}
                      className={`py-2 px-3 rounded-lg border text-xs font-semibold transition-colors ${
                        settings.mode === mode
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300'
                          : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      {mode === 'snapshot' ? 'Manual snapshot' : 'Auto-refresh'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Refresh interval (always-ready only) */}
              {settings.mode === 'always-ready' && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">Refresh every</p>
                  <div className="flex flex-wrap gap-1.5">
                    {REFRESH_INTERVAL_OPTIONS.map((mins) => (
                      <button
                        key={mins}
                        type="button"
                        onClick={() => handleSave({ refreshIntervalMinutes: mins })}
                        className={`px-2.5 py-1 rounded-md border text-xs font-semibold transition-colors ${
                          settings.refreshIntervalMinutes === mins && !isCustomInterval
                            ? 'border-indigo-500 bg-indigo-600 text-white'
                            : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-indigo-400'
                        }`}
                      >
                        {mins}m
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setCustomInterval(String(settings.refreshIntervalMinutes))}
                      className={`px-2.5 py-1 rounded-md border text-xs font-semibold transition-colors ${
                        isCustomInterval
                          ? 'border-indigo-500 bg-indigo-600 text-white'
                          : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-indigo-400'
                      }`}
                    >
                      Custom
                    </button>
                  </div>
                  {(isCustomInterval || customInterval !== '') && (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="1440"
                        value={customInterval || String(settings.refreshIntervalMinutes)}
                        onChange={(e) => setCustomInterval(e.target.value)}
                        onBlur={() => {
                          const val = Math.max(1, Math.min(1440, parseInt(customInterval, 10) || 15));
                          handleSave({ refreshIntervalMinutes: val });
                          setCustomInterval('');
                        }}
                        className="w-20 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-xs text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        placeholder="mins"
                      />
                      <span className="text-xs text-slate-500">minutes (1–1440)</span>
                    </div>
                  )}
                </div>
              )}

              {/* Snapshot status */}
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Snapshot status</span>
                  {lastSnap ? (
                    <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {formatSnapshotAge(lastSnap)}
                    </span>
                  ) : (
                    <span className="text-xs text-amber-600 dark:text-amber-400">No snapshot yet</span>
                  )}
                </div>

                {snapErr && (
                  <p className="text-xs text-red-600 dark:text-red-400">{snapErr}</p>
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void triggerSnapshot()}
                    disabled={snapping}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-colors disabled:opacity-60"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${snapping ? 'animate-spin' : ''}`} />
                    {snapping ? 'Snapshotting…' : lastSnap ? 'Update snapshot' : 'Snapshot now'}
                  </button>
                  {lastSnap && (
                    <button
                      type="button"
                      onClick={() => void handleClear()}
                      className="p-1.5 rounded-md text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      title="Clear snapshot"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
